import { Field } from "formik";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  ClipboardDocumentIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ClipboardDocumentCheckIcon } from "@heroicons/react/20/solid";
import { fetchBringPostalNumber } from "../../api/external/bring";
import {
  fetchBrregOrganization,
  searchBrregEntitiesByName,
} from "../../api/external/brreg";
import {
  searchAddresses,
  getPlaceDetails,
} from "../../api/external/googleMaps";
import debounce from "lodash/debounce";
import { TWclassNames } from "./Div";

/** Local part + domain; strips paste noise (commas, extra addresses, angle brackets). */
const EMAIL_STRIP_DISALLOWED = /[^a-zA-Z0-9@._+-]/g;

function fieldLeaf(fieldName) {
  if (!fieldName || typeof fieldName !== "string") return "";
  return fieldName.includes(".") ? fieldName.split(".").pop() : fieldName;
}

function isEmailField(fieldName, inputType) {
  if (inputType === "email") return true;
  return fieldLeaf(fieldName) === "email";
}

function isPrimaryNameField(fieldName) {
  return fieldLeaf(fieldName) === "name";
}

function siblingFieldPath(fieldPath, leafName) {
  if (!fieldPath || !leafName) return leafName;
  if (!fieldPath.includes(".")) return leafName;
  const parts = fieldPath.split(".");
  parts[parts.length - 1] = leafName;
  return parts.join(".");
}

function getFormikNestedValue(values, path) {
  if (!values || !path) return undefined;
  if (!path.includes(".")) return values[path];
  let cur = values;
  for (const p of path.split(".")) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function extractFirstEmailSegment(raw) {
  if (typeof raw !== "string") return "";
  let s = raw.trim();
  const angle = s.match(/<([^\s<>]+@[^\s<>]+\.[^\s<>]+)>/);
  if (angle) return angle[1].trim();
  const first = s.split(/[,;]/)[0]?.trim() || "";
  return first;
}

export function sanitizeEmailInput(raw) {
  let s = extractFirstEmailSegment(raw);
  s = s.replace(/\s/g, "");
  s = s.replace(EMAIL_STRIP_DISALLOWED, "");
  return s;
}

export function isValidEmailFormat(s) {
  if (!s || typeof s !== "string") return false;
  return /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(s);
}

function looksLikeEmailAddress(s) {
  if (!s || typeof s !== "string") return false;
  const t = s.trim();
  return /^[^\s<>,;]+@[^\s<>,;]+\.[^\s<>,;]+$/.test(t) || /<[^\s<>]+@[^\s<>]+\.[^\s<>]+>/.test(t);
}

const PMTextArea = (props) => {
  return <textarea {...props} />;
};

export const PMField = (props) => {
  const {
    type = "text",
    name,
    label,
    multiline = false,
    touched,
    errors,
    onBlur,
    onChange,
    value,
    values,
    readOnly = false,
    onClick,
    setFieldValue,
    description,
    disabled,
    placeholder,
    checked,
    className,
    formik = true,
    variant = "default",
    ...other
  } = props;

  const { t } = useTranslation();

  const [isCopied, setIsCopied] = useState(false);
  const [brregSuggestions, setBrregSuggestions] = useState([]);
  const [isSearchingBrreg, setIsSearchingBrreg] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const suppressNextNameSearchRef = useRef(false);
  const debouncedNameSearchRef = useRef(null);
  const debouncedAddressSearchRef = useRef(null);
  const hasUserTypedNameRef = useRef(false);
  const hasUserTypedAddressRef = useRef(false);

  // Initialize debounced search once
  useEffect(() => {
    debouncedNameSearchRef.current = debounce(async (query) => {
      try {
        setIsSearchingBrreg(true);
        const res = await searchBrregEntitiesByName(query, 10, 0);
        const list = res?.data?._embedded?.enheter || res?.data?.enheter || [];
        const mapped = list.map((e) => ({
          organisasjonsnummer: e?.organisasjonsnummer,
          navn: e?.navn,
          raw: e,
        }));
        setBrregSuggestions(mapped);
      } catch (e) {
        setBrregSuggestions([]);
      } finally {
        setIsSearchingBrreg(false);
      }
    }, 400);

    debouncedAddressSearchRef.current = debounce(async (query) => {
      try {
        setIsSearchingAddress(true);
        const results = await searchAddresses(query);
        setAddressSuggestions(results || []);
      } catch (e) {
        console.error("Error searching addresses:", e);
        setAddressSuggestions([]);
      } finally {
        setIsSearchingAddress(false);
      }
    }, 400);

    return () => {
      if (debouncedNameSearchRef.current) debouncedNameSearchRef.current.cancel();
      if (debouncedAddressSearchRef.current) debouncedAddressSearchRef.current.cancel();
    };
  }, []);

  // Ensure we always have a defined value to prevent controlled/uncontrolled switching
  const getFieldValue = () => {
    // Handle nested field names like "physical_address.address"
    if (values && name?.includes(".")) {
      const parts = name.split(".");
      let nestedValue = values;
      for (const part of parts) {
        if (nestedValue && nestedValue[part] !== undefined) {
          nestedValue = nestedValue[part];
        } else {
          nestedValue = undefined;
          break;
        }
      }
      if (nestedValue !== undefined) {
        return nestedValue;
      }
    }
    
    if (values && values[name] !== undefined) {
      return values[name];
    }
    if (value !== undefined) {
      return value;
    }
    // For checkboxes, return false as default, for other fields return empty string
    return type === "checkbox" ? false : "";
  };

  const fieldValue = getFieldValue();

  const handleChange = (e) => {
    const fieldName = e?.target?.name || name;
    
    if (type == "number") {
      e.target.value = e.target.value.replace(/,/g, ".").replace(/\s/g, "");
    }
    if (isEmailField(fieldName, type) && typeof e.target.value === "string") {
      e.target.value = sanitizeEmailInput(e.target.value);
    }
    // Force VAT field to contain only digits and max 9 chars
    if (fieldName === "vat" || fieldName?.endsWith(".vat")) {
      e.target.value = e.target.value.replace(/\D/g, "").slice(0, 9);
    }
    // Mark when user actually types in the name field (prevents auto-open on mount)
    if (fieldName === "name" || fieldName?.endsWith(".name")) {
      hasUserTypedNameRef.current = true;
    }
    // Mark when user actually types in an address field
    if (fieldName === "address" || fieldName?.endsWith(".address")) {
      hasUserTypedAddressRef.current = true;
    }

    if (onChange) {
      onChange(e);
    }

    if (setFieldValue) {
      if (type === "checkbox") {
        setFieldValue(name, e.target.checked);
      } else {
        setFieldValue(name, e.target.value);
      }
    }
  };

  const handleFieldBlur = (e) => {
    setBrregSuggestions([]);
    setAddressSuggestions([]);

    const blurVal = typeof e?.target?.value === "string" ? e.target.value : "";

    if (isEmailField(name, type)) {
      const cleaned = sanitizeEmailInput(blurVal);
      if (setFieldValue && cleaned !== blurVal) {
        setFieldValue(name, cleaned);
      }
      if (cleaned && !isValidEmailFormat(cleaned)) {
        toast.error(t("pmField.email.invalid"));
      } else if (cleaned) {
        const namePath = siblingFieldPath(name, "name");
        const nameVal = getFormikNestedValue(values, namePath);
        if (
          typeof nameVal === "string" &&
          nameVal.trim().length > 0 &&
          cleaned.toLowerCase() === nameVal.trim().toLowerCase()
        ) {
          toast.error(t("pmField.email.sameAsName"));
        }
      }
    }

    if (isPrimaryNameField(name)) {
      const v = blurVal.trim();
      if (v && looksLikeEmailAddress(v)) {
        toast(t("pmField.email.nameLooksLikeEmail"), { icon: "⚠️" });
      }
      const emailPath = siblingFieldPath(name, "email");
      const emailVal = getFormikNestedValue(values, emailPath);
      if (
        v &&
        typeof emailVal === "string" &&
        emailVal.trim().length > 0 &&
        v.toLowerCase() === emailVal.trim().toLowerCase()
      ) {
        toast.error(t("pmField.email.sameAsName"));
      }
    }

    if (onBlur) onBlur(e);
  };

  const copyToClipboard = (value) => {
    if (!value) {
      setIsCopied(false);
      toast.error("Cannot copy an empty field");
    } else {
      navigator.clipboard.writeText(value);
      setIsCopied(true);
    }
  };

  const placeName = name?.includes(".")
    ? name.split(".")[0] + ".place"
    : "place";

  // const pnr = name.includes(".")
  //   ? values[name.split(".")[0]]["postal"]
  //   : values["postal"];

  function getPnr() {
    if (name?.includes(".")) {
      return values[name.split(".")[0]]["postal"];
    } else if (name == "postal") {
      return values["postal"];
    } else {
      return "";
    }
  }

  const pnr = getPnr();

  async function getPlace() {
    var res = null;
    if (pnr !== "" && pnr?.length > 3) {
      try {
        res = await fetchBringPostalNumber(pnr);
        setFieldValue(placeName, res?.data?.result);
      } catch (error) {
        console.error("Error fetching place:", error);
      }
    }
  }

  useEffect(() => {
    const lastName = name?.includes(".") ? name.split(".")[1] : name;
    if (lastName?.includes("postal") && pnr !== "") {
      const res = getPlace();
    }
  }, [pnr]);

  // Helper: apply address data from Google Maps to form fields
  const applyAddressDataToForm = (addressData, addressFieldName) => {
    if (!setFieldValue || !addressFieldName) return;

    const basePath = addressFieldName.includes(".")
      ? addressFieldName.split(".").slice(0, -1).join(".")
      : "";

    const getFieldPath = (field) => {
      return basePath ? `${basePath}.${field}` : field;
    };

    // Set address line - use addressLine if available, otherwise use formattedAddress
    const addressToSet = addressData.addressLine || addressData.formattedAddress || "";
    if (addressToSet) {
      setFieldValue(getFieldPath("address"), addressToSet);
    }

    // Set postal code
    if (addressData.postalCode) {
      const postalField = getFieldPath("postal");
      setFieldValue(postalField, addressData.postalCode);
    }

    // Set city/place
    if (addressData.city) {
      const placeField = getFieldPath("place");
      setFieldValue(placeField, addressData.city);
    }
  };

  // Helper: apply Brreg organization data into the form (overwrite existing values)
  const applyBrregDataToForm = (data) => {
    const orgName = data?.navn || data?.name || "";
    const postal = data?.postadresse || data?.postalAddress || {};
    const business = data?.forretningsadresse || data?.businessAddress || {};

    const joinAddressLines = (addr) => {
      const lines = (addr?.adresse || addr?.address || []) || [];
      return Array.isArray(lines) ? lines.filter(Boolean).join(", ") : lines;
    };

    if (setFieldValue) {
      // Always overwrite available fields
      if (orgName) setFieldValue("name", orgName);

      // If no postal address, fall back to business address
      const hasPostal = Boolean(
        (postal?.adresse && postal?.adresse?.length) || postal?.postnummer || postal?.postnr || postal?.postCode
      );
      const postalSource = hasPostal ? postal : business;

      const postalAddrLine = joinAddressLines(postalSource);
      const postalPostnr = postalSource?.postnummer || postalSource?.postnr || postalSource?.postCode || "";
      const postalPoststed = postalSource?.poststed || postalSource?.poststednavn || postalSource?.city || "";

      if (postalAddrLine) setFieldValue("postal_address.address", postalAddrLine);
      if (postalPostnr) setFieldValue("postal_address.postal", postalPostnr);
      if (postalPoststed) setFieldValue("postal_address.place", postalPoststed);

      const businessAddrLine = joinAddressLines(business);
      const businessPostnr = business?.postnummer || business?.postnr || business?.postCode || "";
      const businessPoststed = business?.poststed || business?.poststednavn || business?.city || "";

      if (businessAddrLine) setFieldValue("physical_address.address", businessAddrLine);
      if (businessPostnr) setFieldValue("physical_address.postal", businessPostnr);
      if (businessPoststed) setFieldValue("physical_address.place", businessPoststed);
    }
  };

  // Removed VAT auto-suggestion; VAT lookup is manual via refresh icon

  // Search and suggestion logic for Name (company name) — only for business customers
  useEffect(() => {
    const lastName = name?.includes(".") ? name.split(".")[1] : name;
    const isNameField = lastName === "name" || name === "name";
    if (!isNameField) return;

    const valueToCheck = typeof fieldValue === "string" ? fieldValue : "";
    const typeHint = values?.customer_type || values?.customer;
    const isBusinessType = [
      "company",
      "supplier",
      "customer_company_supplier",
      "customer",
    ].includes(typeHint);

    if (!isBusinessType) {
      setBrregSuggestions([]);
      return;
    }

    // Do not fetch/open suggestions until the user actually types in the name field
    if (!hasUserTypedNameRef.current) {
      setBrregSuggestions([]);
      return;
    }

    if (!valueToCheck || valueToCheck.trim().length < 2) {
      setBrregSuggestions([]);
      return;
    }

    if (suppressNextNameSearchRef.current) {
      suppressNextNameSearchRef.current = false;
      setBrregSuggestions([]);
      return;
    }

    if (debouncedNameSearchRef.current) {
      debouncedNameSearchRef.current.cancel();
      debouncedNameSearchRef.current(valueToCheck);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue, name, values]);

  // Search and suggestion logic for Address fields using Google Maps
  useEffect(() => {
    const lastName = name?.includes(".") ? name.split(".")[1] : name;
    // Check if this is an address field - must end with ".address" or be exactly "address"
    const isAddressField = lastName === "address" || name?.endsWith(".address");
    
    if (!isAddressField) {
      return;
    }

    // Get current field value (recalculate in case values changed)
    const currentValue = getFieldValue();
    const valueToCheck = typeof currentValue === "string" ? currentValue : "";

    // Do not fetch/open suggestions until the user actually types in the address field
    if (!hasUserTypedAddressRef.current) {
      return;
    }

    if (!valueToCheck || valueToCheck.trim().length < 2) {
      setAddressSuggestions([]);
      return;
    }

    if (debouncedAddressSearchRef.current) {
      debouncedAddressSearchRef.current.cancel();
      debouncedAddressSearchRef.current(valueToCheck);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue, name, values]);

  const hasError = Boolean(touched && touched[name] && errors && errors[name]);

  if (type === "checkbox") {
    return (
      <div className="relative flex items-start">
        <div className="flex items-center">
          <input
            ref={props?.ref}
            disabled={readOnly || disabled}
            id={name}
            name={name}
            type="checkbox"
            checked={fieldValue || false}
            onChange={(data) => handleChange(data)}
            className="pm-checkbox"
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor={name} className="pm-label">
            {t(label)}
          </label>
          {description && <p className="pm-help">{t(description)}</p>}
        </div>
      </div>
    );
  } else {
    return (
      <div>
        <label
          htmlFor={name}
          className="pm-label"
        >
          <label> {t(label)} </label>
          {description && <p className="pm-help">{t(description)}</p>}
        </label>
        <div className="relative mt-2">
          {setFieldValue ? (
            <Field
              ref={props?.ref}
              className={TWclassNames("pm-input pr-10", className)}
              aria-invalid={hasError || undefined}
              onClick={onClick}
              type={type}
              id={name}
              name={name}
              onBlur={handleFieldBlur}
              as={multiline ? PMTextArea : "input"}
              disabled={readOnly || disabled}
              placeholder={t(placeholder)}
              onChange={(e) => {
                // Handle both Formik events and standard events
                const event = e?.target ? e : { target: { name, value: e, checked: false } };
                handleChange(event);
              }}
              // {...other}
            />
          ) : (
            <input
              ref={props?.ref}
              className={TWclassNames("pm-input pr-10", className)}
              aria-invalid={hasError || undefined}
              onClick={onClick}
              onChange={handleChange}
              type={type}
              id={name}
              name={name}
              onBlur={handleFieldBlur}
              disabled={readOnly || disabled}
              placeholder={t(placeholder)}
              value={fieldValue}
            />
          )}
          {/* Refresh icon for VAT field */}
          {!readOnly && (name === "vat" || name?.endsWith(".vat")) ? (
            <div
              className="cursor-pointer absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={async () => {
                const vat = (typeof fieldValue === "string" ? fieldValue : "").replace(/\D/g, "");
                if (!/^\d{9}$/.test(vat)) {
                  toast.error(t("Enter 9-digit org. number"));
                  return;
                }
                try {
                  const res = await fetchBrregOrganization(vat);
                  const data = res?.data || {};
                  applyBrregDataToForm(data);
                  toast.success(t("Company data refreshed from Brreg"));
                } catch (e) {
                  toast.error(t("Could not refresh from Brreg"));
                }
              }}
              title={t("Refresh from Brreg")}
            >
              <ArrowPathIcon className="text-gray-400 h-5 w-5 hover:text-blue-400" aria-hidden="true" />
            </div>
          ) : null}

          {readOnly ? (
            <div
              className="cursor-pointer absolute inset-y-0 right-0 flex items-center pr-3 "
              onClick={() => {
                copyToClipboard(fieldValue);
              }}
            >
              {isCopied ? (
                <ClipboardDocumentCheckIcon
                  className="text-green-500 h-5 w-5"
                  aria-hidden="true"
                />
              ) : (
                <ClipboardDocumentIcon
                  className="text-gray-400 h-5 w-5 hover:text-blue-400"
                  aria-hidden="true"
                />
              )}
            </div>
          ) : null}
          {/* Suggestions dropdown for Address fields */}
          {!readOnly && (name === "address" || name?.endsWith(".address")) && addressSuggestions?.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 shadow-lg rounded max-h-56 overflow-auto">
              {addressSuggestions.map((s, idx) => (
                <div
                  key={`${s?.placeId}-${idx}`}
                  className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
                  onMouseDown={async (e) => {
                    e.preventDefault();
                    try {
                      const placeDetails = await getPlaceDetails(s.placeId);
                      // Auto-fill related address fields (this will also set the address field)
                      applyAddressDataToForm(placeDetails, name);
                      toast.success(t("Address selected"));
                      setAddressSuggestions([]);
                      // Reset the flag so suggestions don't immediately reappear
                      hasUserTypedAddressRef.current = false;
                    } catch (err) {
                      console.error("Error loading address details:", err);
                      // Fallback: just set the description
                      if (setFieldValue) {
                        setFieldValue(name, s.description);
                      }
                      toast.error(t("Could not load address details"));
                    }
                  }}
                >
                  <div className="font-medium">{s.mainText}</div>
                  {s.secondaryText && (
                    <div className="text-xs text-gray-500">{s.secondaryText}</div>
                  )}
                </div>
              ))}
            </div>
          )}
          {/* Suggestions dropdown for VAT or Name */}
          {!readOnly && (name === "name" || name?.endsWith(".name")) && brregSuggestions?.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 shadow-lg rounded max-h-56 overflow-auto">
              {brregSuggestions.map((s, idx) => (
                <div
                  key={`${s?.organisasjonsnummer}-${idx}`}
                  className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700"
                  onMouseDown={async (e) => {
                    e.preventDefault();
                    try {
                      const orgnr = s?.organisasjonsnummer || (typeof fieldValue === "string" ? fieldValue : "");
                      let data = s?.raw || {};
                      if (!data?.organisasjonsnummer) {
                        const res = await fetchBrregOrganization(orgnr);
                        data = res?.data || {};
                      }
                      // Ensure VAT field gets set when selecting from suggestions
                      if (setFieldValue && orgnr) setFieldValue("vat", orgnr);
                      // Ensure name reflects selected company
                      if (setFieldValue && (s?.navn || s?.name)) setFieldValue("name", s?.navn || s?.name);
                      applyBrregDataToForm(data);
                      toast.success(t("Company selected from Brreg"));
                      suppressNextNameSearchRef.current = true;
                      setBrregSuggestions([]);
                    } catch (err) {
                      toast.error(t("Could not load company from Brreg"));
                    }
                  }}
                >
                  <div className="font-medium">{s?.navn || s?.name}</div>
                  <div className="text-xs text-gray-500">{s?.organisasjonsnummer}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {hasError && (
          <div className="pm-field-error" role="alert">
            <ExclamationCircleIcon className="w-4 h-4 flex-none" aria-hidden="true" />
            <span>
              {typeof errors[name] === "string" || typeof errors[name] === "number"
                ? String(errors[name])
                : Array.isArray(errors[name])
                  ? errors[name].join(", ")
                  : ""}
            </span>
          </div>
        )}
      </div>
    );
  }
};



PMField.propTypes = {
  type: PropTypes.string,
  name: PropTypes.string,
  multiline: PropTypes.bool,
  readOnly: PropTypes.bool,
};
